import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getCategories, getProducts } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useCart } from '@/lib/cart';
import { Search, Package, ShoppingCart, ChevronLeft, ChevronRight, Filter, X } from 'lucide-react';

export default function Catalog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isPro, isAdmin } = useAuth();
  const { addToCart } = useCart();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const activeCategory = searchParams.get('category') || '';
  const searchQuery = searchParams.get('search') || '';
  const currentPage = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    getCategories().then(r => setCategories(r.data.filter(c => c.product_count > 0)));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { page: currentPage, limit: 20 };
    if (activeCategory) params.category = activeCategory;
    if (searchQuery) params.search = searchQuery;
    getProducts(params).then(r => {
      setProducts(r.data.products);
      setTotal(r.data.total);
      setPages(r.data.pages);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [activeCategory, searchQuery, currentPage]);

  const setCategory = (slug) => {
    const params = new URLSearchParams(searchParams);
    if (slug) { params.set('category', slug); } else { params.delete('category'); }
    params.delete('page');
    setSearchParams(params);
  };

  const setSearch = (q) => {
    const params = new URLSearchParams(searchParams);
    if (q) { params.set('search', q); } else { params.delete('search'); }
    params.delete('page');
    setSearchParams(params);
  };

  const goToPage = (p) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', p.toString());
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddToCart = async (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    try { await addToCart(productId); } catch {}
  };

  const activeCatName = categories.find(c => c.slug === activeCategory)?.name || 'Tous les produits';

  return (
    <div data-testid="catalog-page" className="min-h-screen">
      {/* Page Header */}
      <div className="border-b border-black/10 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-xs uppercase tracking-[0.2em] text-[#00A5FF] font-semibold mb-2">Catalogue</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#111] tracking-tight">{activeCatName}</h1>
          <p className="text-sm text-black/50 mt-1">{total} produit{total !== 1 ? 's' : ''}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Sidebar - Desktop */}
          <aside className="hidden lg:block w-56 shrink-0">
            {/* Search */}
            <div className="relative mb-4">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/30" />
              <input
                type="text"
                placeholder="Rechercher..."
                defaultValue={searchQuery}
                onKeyDown={(e) => { if (e.key === 'Enter') setSearch(e.target.value); }}
                data-testid="search-input"
                className="w-full pl-9 pr-3 py-2 text-sm border border-black/15 bg-white focus:ring-2 focus:ring-[#00A5FF] focus:border-transparent outline-none transition-shadow duration-200"
              />
            </div>

            {/* Categories */}
            <div className="border border-black/10 bg-white">
              <div className="p-3 border-b border-black/10">
                <h3 className="text-xs uppercase tracking-[0.15em] font-semibold text-black/60">Catégories</h3>
              </div>
              <div className="p-1">
                <button
                  onClick={() => setCategory('')}
                  data-testid="category-all"
                  className={`w-full text-left px-3 py-2 text-sm rounded-sm transition-colors duration-200 ${!activeCategory ? 'bg-[#00A5FF] text-white font-semibold' : 'text-[#111] hover:bg-[#F8F9FA]'}`}
                >
                  Tous les produits
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => setCategory(cat.slug)}
                    data-testid={`filter-${cat.slug}`}
                    className={`w-full text-left px-3 py-2 text-sm rounded-sm transition-colors duration-200 flex justify-between items-center ${activeCategory === cat.slug ? 'bg-[#00A5FF] text-white font-semibold' : 'text-[#111] hover:bg-[#F8F9FA]'}`}
                  >
                    <span className="truncate">{cat.name}</span>
                    <span className={`text-xs ${activeCategory === cat.slug ? 'text-white/70' : 'text-black/30'}`}>{cat.product_count}</span>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Mobile Search + Filter */}
            <div className="lg:hidden flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/30" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  defaultValue={searchQuery}
                  onKeyDown={(e) => { if (e.key === 'Enter') setSearch(e.target.value); }}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-black/15 focus:ring-2 focus:ring-[#00A5FF] outline-none"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-3 py-2 border border-black/15 text-sm font-medium flex items-center gap-1"
              >
                <Filter size={14} /> Filtres
              </button>
            </div>

            {/* Mobile Filters */}
            {showFilters && (
              <div className="lg:hidden mb-4 border border-black/10 p-3 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs uppercase tracking-wider font-semibold">Catégories</h3>
                  <button onClick={() => setShowFilters(false)}><X size={16} /></button>
                </div>
                <div className="flex flex-wrap gap-1">
                  <button onClick={() => { setCategory(''); setShowFilters(false); }} className={`px-3 py-1 text-xs rounded-sm border ${!activeCategory ? 'bg-[#00A5FF] text-white border-[#00A5FF]' : 'border-black/15 text-[#111]'}`}>Tous</button>
                  {categories.map((cat) => (
                    <button key={cat.slug} onClick={() => { setCategory(cat.slug); setShowFilters(false); }} className={`px-3 py-1 text-xs rounded-sm border ${activeCategory === cat.slug ? 'bg-[#00A5FF] text-white border-[#00A5FF]' : 'border-black/15 text-[#111]'}`}>
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Active filters */}
            {(activeCategory || searchQuery) && (
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {activeCategory && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#00A5FF]/10 text-[#00A5FF] text-xs font-medium">
                    {activeCatName}
                    <button onClick={() => setCategory('')}><X size={12} /></button>
                  </span>
                )}
                {searchQuery && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#F5C400]/15 text-[#111] text-xs font-medium">
                    "{searchQuery}"
                    <button onClick={() => setSearch('')}><X size={12} /></button>
                  </span>
                )}
              </div>
            )}

            {/* Products Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-px bg-black/10 border border-black/10">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white p-4 animate-pulse">
                    <div className="aspect-square bg-[#F8F9FA] mb-3" />
                    <div className="h-3 bg-[#F8F9FA] w-20 mb-2" />
                    <div className="h-4 bg-[#F8F9FA] w-full mb-2" />
                    <div className="h-5 bg-[#F8F9FA] w-16" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20 border border-black/10 bg-white">
                <Package size={48} className="mx-auto text-black/10 mb-4" />
                <p className="text-sm text-black/50">Aucun produit trouvé</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-px bg-black/10 border border-black/10">
                {products.map((p) => (
                  <Link
                    key={p.id}
                    to={`/product/${p.id}`}
                    data-testid={`catalog-product-${p.id}`}
                    className="group bg-white p-4 hover:shadow-md transition-shadow duration-200 flex flex-col"
                  >
                    <div className="aspect-square bg-[#F8F9FA] border border-black/5 mb-3 flex items-center justify-center relative overflow-hidden">
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <Package size={36} className="text-black/10" />
                      )}
                      {user && (
                        <button
                          onClick={(e) => handleAddToCart(e, p.id)}
                          data-testid={`add-to-cart-${p.id}`}
                          className="absolute bottom-2 right-2 p-2 bg-[#00A5FF] text-white rounded-sm opacity-0 group-hover:opacity-100 hover:bg-[#0090e0] transition-all duration-200"
                        >
                          <ShoppingCart size={14} />
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-[#00A5FF] font-semibold uppercase tracking-wider mb-1">{p.category}</p>
                    <h3 className="text-sm font-bold text-[#111] leading-tight group-hover:text-[#00A5FF] transition-colors duration-200 line-clamp-2 mb-auto">
                      {p.name}
                    </h3>
                    <div className="mt-3 pt-3 border-t border-black/5">
                      <div className="flex items-baseline justify-between">
                        <div>
                          <span className="text-lg font-extrabold text-[#111]">{p.public_price?.toFixed(2)}</span>
                          <span className="text-xs text-black/50 ml-1">EUR</span>
                        </div>
                        {p.pro_price && (
                          <span className="px-2 py-0.5 bg-[#F5C400]/15 text-[#111] text-xs font-bold rounded-sm">
                            Pro: {p.pro_price.toFixed(2)}
                          </span>
                        )}
                      </div>
                      {!user && (
                        <p className="text-xs text-black/40 mt-1">Connectez-vous pour le prix pro</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="p-2 border border-black/15 disabled:opacity-30 hover:bg-[#F8F9FA] transition-colors duration-200"
                >
                  <ChevronLeft size={16} />
                </button>
                {[...Array(pages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goToPage(i + 1)}
                    className={`w-9 h-9 text-sm font-medium border transition-colors duration-200 ${currentPage === i + 1 ? 'bg-[#00A5FF] text-white border-[#00A5FF]' : 'border-black/15 text-[#111] hover:bg-[#F8F9FA]'}`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= pages}
                  className="p-2 border border-black/15 disabled:opacity-30 hover:bg-[#F8F9FA] transition-colors duration-200"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
